import { useUserInfo } from "@features/auth/useAuth";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import type { HomePageDataQuery, HubService } from "./__generated__/HomePageDataQuery.graphql";

const partnersQuery = graphql`
	query HomePageDataQuery($includeGrants: Boolean!, $organizationId: UUID!, $includeOrganization: Boolean!) {
		onedeliveryAccess @include(if: $includeGrants) @catch(to: NULL)
		organization(id: $organizationId) @include(if: $includeOrganization) @catch(to: NULL) {
			id
			scopes
		}
		myUtilizationGrants(input: {}, pagination: { page: 0, pageSize: 1000 }) @include(if: $includeGrants) @catch(to: NULL) {
			nodes {
				deactivatedAt
				reservation {
					deactivatedAt
					flow
					subtype {
						__typename
					}
				}
			}
		}
		hubMetas(pagination: { page: 0, pageSize: 1000000 }) {
			nodes {
				id
				organization {
					id
				}
				hub {
					id
					meta {
						services
					}
				}
				serviceProviders {
					id
					name
					logo {
						id
						variant(variant: PUBLIC)
					}
				}
			}
		}
	}
`;

export const useHomePageData = (scannedHubId?: string) => {
	const user = useUserInfo();
	const organizationId = user?.organization?.id;
	const data = useLazyLoadQuery<HomePageDataQuery>(partnersQuery, {
		includeGrants: Boolean(user),
		organizationId: organizationId ?? "00000000-0000-0000-0000-000000000000",
		includeOrganization: Boolean(organizationId),
	});

	const partners =
		data.hubMetas?.nodes
			.flatMap((hub) => hub?.serviceProviders ?? [])
			.filter((org, index, all) => all.findIndex((item) => item?.id === org?.id) === index)
			.filter((org) => Boolean(org))
			.filter((org) => org.logo?.variant)
			.map((org) => ({
				id: org.id,
				name: org.name,
				logoSrc: org.logo!.variant,
			})) ?? [];

	const scannedHubMeta = data.hubMetas?.nodes.find((hubMeta) => hubMeta?.hub?.id === scannedHubId);
	const scannedHubServices: readonly HubService[] | null | undefined = scannedHubMeta?.hub?.meta?.services;
	const availableServices = new Set(data.hubMetas?.nodes.flatMap((hubMeta) => hubMeta?.hub?.meta?.services ?? []) ?? []);
	const activeB2BGrants =
		data.myUtilizationGrants?.nodes.filter(
			(grant) =>
				!grant.deactivatedAt && !grant.reservation.deactivatedAt && grant.reservation.subtype.__typename === "B2BlockReservationSubtype"
		) ?? [];
	const hasManagedCompartmentAccess = activeB2BGrants.some((grant) => grant.reservation.flow === "SWAP");
	// Goods reception is offered to organizations with the service, to the owner and the service
	// providers of a hub with Lock B2B (they can deliver into it: the scanned hub, or any hub when
	// none is scanned), and to recipients of a delivery.
	type HubMetaNode = NonNullable<NonNullable<typeof data.hubMetas>["nodes"][number]>;
	const deliversInto = (hubMeta: HubMetaNode | null | undefined) =>
		Boolean(organizationId) &&
		Boolean(hubMeta?.hub?.meta?.services?.includes("LOCK_B2B")) &&
		(hubMeta?.organization.id === organizationId || Boolean(hubMeta?.serviceProviders.some((provider) => provider?.id === organizationId)));
	const deliversIntoScannedHub = scannedHubId
		? deliversInto(scannedHubMeta)
		: Boolean(data.hubMetas?.nodes.some((hubMeta) => deliversInto(hubMeta)));
	const hasWarenannahmeAccess =
		Boolean(data.organization?.scopes.includes("WARENANNAHME")) ||
		deliversIntoScannedHub ||
		activeB2BGrants.some((grant) => grant.reservation.flow === "GOODS_RECEPTION");

	return {
		partners,
		scannedHubServices,
		isScannedHubResolved: !scannedHubId || Boolean(scannedHubMeta),
		availableServices,
		hasManagedCompartmentAccess,
		hasWarenannahmeAccess,
		hasOneDeliveryAccess: Boolean(data.onedeliveryAccess),
	};
};
